import React, { useRef, useState, useEffect, useMemo } from 'react';

interface CompositionItem {
    quantity: number;
    percentage: number;
}

interface CompositionData {
    [key: string]: CompositionItem;
}

interface ProductCompositionProps {
    data: string;
    locale?: string;
    unitOfMeasure?: string;
    isThinMode?: boolean;
    canViewPrice?: boolean;
    usageContext?: 'cart' | 'eve-checkout' | 'other';
    showPercentage?: boolean;
}

const NomenclatureBundleDetails: React.FC<ProductCompositionProps> = ({
    data,
    locale = 'fr-CA',
    unitOfMeasure = '',
    isThinMode = false,
    canViewPrice = false,
    usageContext = 'other',
    showPercentage = false
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isNarrow, setIsNarrow] = useState(false);
    const [isWideWrapper, setIsWideWrapper] = useState(false);

    const parsedData = useMemo(() => {
        try {
            const parsed = JSON.parse(data);
            return parsed as CompositionData;
        } catch (e) {
            console.error('Error while parsing composition JSON', e);
            return {} as CompositionData;
        }
    }, [data]);

    useEffect(() => {
        const checkWidth = () => {
            if (wrapperRef.current) {
                setIsNarrow(wrapperRef.current.offsetWidth < 150);
                setIsWideWrapper(wrapperRef.current.offsetWidth > 430);
            }
        };

        checkWidth();
        window.addEventListener('resize', checkWidth);

        return () => {
            window.removeEventListener('resize', checkWidth);
        };
    }, []);

    if (!parsedData || Object.keys(parsedData).length === 0) {
        return null;
    }

    const isEnglish = locale === 'en' || locale?.startsWith('en-');
    const title = isEnglish ? 'Targeted composition' : 'Composition ciblée';

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3
        }).format(num);
    };

    const styles: { [key: string]: React.CSSProperties } = {
        wrapper: {
            display: 'flex',
            lineHeight: 1.4,
            justifyContent: usageContext === 'cart' ? 'left' : isWideWrapper ? 'center' : isThinMode ? 'left' : 'center',
            marginTop:
                usageContext === 'cart'
                    ? '0px'
                    : isWideWrapper && isThinMode
                    ? canViewPrice
                        ? '-100px'
                        : '-70px'
                    : isNarrow
                    ? '0px'
                    : '-20px',
            marginLeft: usageContext === 'cart' ? '0px' : isWideWrapper && isThinMode ? '-60px' : '',
            marginBottom: '10px',
            width: '100%'
        },
        card: {
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            padding: isThinMode ? '6px' : '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            minWidth: 'max-content',
            fontFamily: '"Avenir", "Helvetica", Arial, sans-serif'
        },
        title: {
            fontSize: '14px',
            fontWeight: 700,
            color: '#000000',
            marginTop: 0,
            marginBottom: '8px',
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '4px'
        },
        list: {
            listStyleType: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: isNarrow ? 'center' : 'flex-start',
            width: '100%'
        },
        listItem: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: isNarrow ? 'center' : 'flex-start',
            padding: isNarrow ? '0' : '6px 0',
            fontSize: '14px',
            color: '#333333',
            flexDirection: isNarrow ? 'column' : 'row'
        },
        icon: {
            marginRight: '6px',
            fontSize: '18px'
        },
        keyText: {
            fontWeight: 600,
            marginRight: isNarrow ? '0' : '8px',
            color: '#000000'
        },
        valueText: {
            fontWeight: 400,
            color: '#555555'
        }
    };

    return (
        <div style={styles.wrapper} ref={wrapperRef}>
            <div style={styles.card}>
                <h2 style={styles.title}>{title}</h2>
                <ul style={styles.list}>
                    {Object.entries(parsedData).map(([key, value]) => {
                        const quantity = value.quantity;
                        const percentage = value.percentage;

                        return (
                            <li key={key} style={styles.listItem}>
                                {!isNarrow && <span style={styles.icon}>📦</span>}
                                <span style={styles.keyText}>
                                    {isNarrow && <span style={styles.icon}>📦</span>}
                                    {key}
                                    {isNarrow ? '' : ':'}
                                </span>
                                <span style={styles.valueText}>
                                    {formatNumber(quantity)} {unitOfMeasure}
                                    {percentage !== undefined && showPercentage && (
                                        <span style={{ marginLeft: '8px', color: '#888888' }}>({formatNumber(percentage)}%)</span>
                                    )}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

export default NomenclatureBundleDetails;
